import { z } from "zod";

/**
 * Trips saved to the guest's account (`/me/trips`).
 *
 * The payload is the trip planner's own `Trip` document (packages/discover
 * `Trip`), stored verbatim. Validation stops at the fields the server reads
 * (id, name, city, dates, party, days and their stops); every other key passes
 * through untouched, so a field the planner gains later is kept instead of
 * being stripped at the boundary and lost on the next sync.
 */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Most days a saved trip may have. The planner offers far fewer. */
export const SAVED_TRIP_MAX_DAYS = 60;
/** Most stops on one day. */
export const SAVED_TRIP_MAX_STOPS_PER_DAY = 100;
/** Most trips one sync call may carry. */
export const SAVED_TRIP_SYNC_MAX = 100;
/** Most party size the server accepts (the planner caps lower). */
export const SAVED_TRIP_MAX_PEOPLE = 50;

export const savedTripStopSchema = z.looseObject({
  id: z.string().min(1).max(128),
  itemId: z.string().min(1).max(128),
});

export const savedTripDaySchema = z.looseObject({
  id: z.string().min(1).max(128),
  label: z.string().max(200),
  date: z.string().max(32).optional(),
  stops: z.array(savedTripStopSchema).max(SAVED_TRIP_MAX_STOPS_PER_DAY),
});

/** The planner's `Trip`, as the account stores it. */
export const savedTripPayloadSchema = z.looseObject({
  id: z.string().min(1).max(128),
  name: z.string().max(200),
  city: z.string().max(120),
  style: z.string().max(40),
  createdAt: z.string().max(64),
  days: z.array(savedTripDaySchema).max(SAVED_TRIP_MAX_DAYS),
  startDate: z.string().regex(ISO_DAY).optional(),
  people: z.number().int().min(1).max(SAVED_TRIP_MAX_PEOPLE).optional(),
});
export type SavedTripPayload = z.infer<typeof savedTripPayloadSchema>;

/** PUT /me/trips/:id */
export const savedTripUpsertSchema = z.object({ trip: savedTripPayloadSchema });
export type SavedTripUpsertInput = z.infer<typeof savedTripUpsertSchema>;

/** POST /me/trips/sync: a device's trips carried onto the account. Additive. */
export const savedTripSyncSchema = z.object({
  trips: z.array(savedTripPayloadSchema).max(SAVED_TRIP_SYNC_MAX),
});
export type SavedTripSyncInput = z.infer<typeof savedTripSyncSchema>;

/** One saved trip as the API returns it. `id` is the trip's own (client) id. */
export const savedTripDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
  city: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  people: z.number().int(),
  dayCount: z.number().int(),
  stopCount: z.number().int(),
  trip: savedTripPayloadSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedTripDto = z.infer<typeof savedTripDtoSchema>;
